import { useCallback, useMemo, useState } from "react";

import * as GeneratorConverter from "@/components/features/_model/converter";

import * as Constants from "./constants";
import * as Type from "./type";
import { voucherInputSchema, type VoucherInputSchema } from "./validation-schema";

import type * as Context from "./context";

type ViewModel = Type.FormModel["ViewModel"];
type InputModel = Type.FormModel["InputModel"];
type QueryData = Type.FormModel["QueryData"];
type ExcludeKeys = Type.ExcludeKeys;
type Props = {
	inputModel: InputModel;
};

export const useFormModel = ({ inputModel: _inputModel }: Props) => {
	const defaultInputModel = useMemo(
		() =>
			Constants.DEFAULT_PRODUCT_VOUCHER_INPUT_MODEL({
				voucherID: _inputModel.voucherID,
				hotelID: _inputModel.hotelID,
			}),
		[_inputModel.hotelID, _inputModel.voucherID],
	);

	const [currentInputModel, setCurrentInputModel] = useState<InputModel>(_inputModel);
	const [viewModel, setViewModel] = useState(
		GeneratorConverter.inputModelToViewModelConverter<InputModel, ExcludeKeys, QueryData, VoucherInputSchema>({
			defaultInput: defaultInputModel,
			initInput: _inputModel,
			currentInput: currentInputModel,
			excludeKeys: Type.excludeKeys,
			zodSchema: voucherInputSchema,
		}),
	);

	const onSetViewModel: Context.MarketingVoucherEditContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} satisfies ViewModel;

			const inputModel = GeneratorConverter.viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const newViewModel = GeneratorConverter.inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				VoucherInputSchema
			>({
				defaultInput: defaultInputModel,
				initInput: currentInputModel,
				currentInput: inputModel,
				excludeKeys: Type.excludeKeys,
				zodSchema: voucherInputSchema,
			});

			setCurrentInputModel(inputModel);
			setViewModel(newViewModel);
		},
		[defaultInputModel, currentInputModel, viewModel],
	);

	/**
	 * @todo ViewModel の変更関数群にまとめる
	 */
	const onChangeInSale = useCallback(
		(value: boolean) => {
			setViewModel({ ...viewModel, inSale: { value, isEdited: true }, isDirty: true });
		},
		[viewModel],
	);
	const onResetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,
		onChangeInSale,
		onResetViewModel,
	};
};
